import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { ApiConfig, GridConfig, GridHeader } from './common-grid.interface';
import {
  ButtonType,
  GRID_DATA_TYPE,
  GridConstants,
  METHOD_TYPE,
} from './common-grid.constant';
import { GridService } from './common-grid.service';
import { LazyLoadEvent, SortEvent } from 'primeng/api';
import { FilterTablePipe } from 'src/app/modules/file/pipe/filter-table.pipe';
import { Table } from 'primeng/table';
import {
  GRID_NAME,
  matchModeOptionsForFilter,
  OrchestrationOptions,
} from 'src/app/core/constants/common';
import { CommonService } from '../../services/common.service';
import { HttpErrorResponse } from '@angular/common/http';
import { PaginationStateService } from '../../services/pagination-state.service';

@Component({
  selector: 'app-common-grid',
  templateUrl: './common-grid.component.html',
  styleUrls: ['./common-grid.component.scss'],
})
export class CommonGridComponent implements OnChanges, OnInit {
  @Input() gridName: string;
  @Input() apiConfig: ApiConfig;
  @Input() headers: Array<GridHeader> = [];
  @Input() gridConfig: GridConfig = {
    pageSizeOptions: [10, 20, 30],
  };
  @Input() enableStateStorage = false; // Enable pagination state preservation
  @Input() selectedChannel?: string; // Current selected channel (for templates)
  @Input() tabType?: string; // Current tab type (ALL/DRAFT for templates, PUBLISHED/DRAFT for campaigns)
  @ViewChild('commonTable') commonTable: Table;

  @Output() onButtonClickEvent: EventEmitter<any> = new EventEmitter();
  @Output() onCellClickEvent: EventEmitter<any> = new EventEmitter();
  @Output() onChannelChangeEvent: EventEmitter<string> = new EventEmitter();
  @Output() onTabChangeEvent: EventEmitter<string> = new EventEmitter();

  gridConstants = new GridConstants();
  matchModeOptions = matchModeOptionsForFilter;
  GRID_DATA_TYPE = GRID_DATA_TYPE;
  BUTTON_TYPE = ButtonType;

  data: any[] = [];
  dataCopy: any[] = [];
  totalRecords = 0;
  totalRecordsInDb = 0;
  sortingDirection: number;
  sortingField: string | undefined;
  currentFilters: any; // Track current applied filters

  pageSize: number;

  // Private properties for state management
  private stateInitialized = false;

  constructor(
    private readonly gridService: GridService,
    private readonly filterPipe: FilterTablePipe,
    private readonly commonService: CommonService,
    private readonly router: Router,
    private readonly paginationStateService: PaginationStateService,
  ) {}

  ngOnInit(): void {
    // State initialization is handled in ngOnChanges when apiConfig is available
  }

  ngOnChanges() {
    if (this.apiConfig?.methodType === METHOD_TYPE.POST) {
      this.pageSize = this.apiConfig.requestBody.pageSize;
    } else if (this.apiConfig?.methodType === METHOD_TYPE.POST1) {
      this.pageSize = this.apiConfig.requestBody.size;
    }

    // Initialize state if not done yet and state storage is enabled
    if (this.enableStateStorage && this.apiConfig && !this.stateInitialized) {
      this.initializeStateFromStorage();
      this.stateInitialized = true;
    }

    // Only reset table if state storage is disabled
    if (this.commonTable && !this.enableStateStorage) {
      this.commonTable.reset();
    }

    this.getGridData();
  }

  getGridData() {
    if (this.apiConfig?.methodType === METHOD_TYPE.POST) {
      this.httpPostCall();
    } else if (this.apiConfig?.methodType === METHOD_TYPE.POST1) {
      this.httpPost1Call();
    }
  }

  httpPost1Call() {
    this.data = [];
    const param = new URLSearchParams(this.apiConfig.requestBody);
    const url = `${this.apiConfig.url}?${param.toString()}`;
    this.gridService.getGridData1(url, {}).subscribe({
      next: (res: any) => {
        if (res.content?.length) {
          const data = res.content;
          if (data?.length) {
            this.data = this.preProcessGridData(data);
          }
          this.dataCopy = [...this.data];
          this.totalRecords = res.totalElements;
          this.totalRecordsInDb = this.totalRecords;
          this.commonTable.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth',
          });
        } else {
          this.data = [];
          this.dataCopy = [...this.data];
          this.totalRecords = 0;
          this.totalRecordsInDb = this.totalRecords;
        }
        // Restore table state after data loading if state storage is enabled
        if (this.enableStateStorage) {
          this.restoreTableState();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.totalRecords = 0;
        this.totalRecordsInDb = 0;
        this.commonService.showError(err);
      },
    });
  }

  httpPostCall() {
    this.data = [];
    const url = this.apiConfig.url;
    this.gridService.getGridData(url, this.apiConfig.requestBody).subscribe({
      next: (res: any) => {
        if (!res.error) {
          const data = res.data.filteredResponse;
          if (data?.length) {
            let isOrchestrationSubTab;
            if (this.gridName === GRID_NAME.API_SCHEDULER_LISTING) {
              isOrchestrationSubTab =
                this.apiConfig.requestBody?.orchestrationType ===
                  OrchestrationOptions.INHOUSE ||
                this.apiConfig.requestBody?.orchestrationType ===
                  OrchestrationOptions.TCL;
            }
            this.data = this.preProcessGridData(data, isOrchestrationSubTab);
          }
          this.dataCopy = [...this.data];
          this.totalRecords = res.data?.page?.totalRecords ?? 0;
          this.totalRecordsInDb = this.totalRecords;
          this.commonTable.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth',
          });
        } else {
          this.data = [];
          this.dataCopy = [...this.data];
          this.totalRecords = 0;
          this.totalRecordsInDb = this.totalRecords;
          this.commonService.showErrorMessage(res.errors[0].errorMessage);
        }
        // Restore table state after data loading if state storage is enabled
        if (this.enableStateStorage) {
          this.restoreTableState();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.totalRecords = 0;
        this.totalRecordsInDb = 0;
        this.commonService.showError(err);
      },
    });
  }

  private handleShortenUrlMockData() {
    const mockResponse = {
      data: {
        page: {
          pageNumber: 1,
          totalPageCount: 124,
          pageSize: 10,
          totalRecords: 1231,
        },
        filteredResponse: [
          {
            code: 'MSIL',
            name: 'Name',
            organization: 'ENGG',
            vertical: 'ENGG',
            division: 'ENGG',
            department: 'ERDS',
            shortUrl: 'www.google.com',
            longUrl: 'MS-MS-sms-inhouse-tiny-bsp-ec',
            expiryDate: '2025-10-18',
            status: 'Active', // Expired / Active
            createdBy: 'Esha Choudhary - echoudary',
            createdDate: '14 Oct 2025',
            statusColClasses: ' executed-status ',
            isCopyView: true,
            isRedirectView: true,
          },
          {
            code: 'MSIL',
            name: 'Name',
            organization: 'ENGG',
            vertical: 'ENGG',
            division: 'ENGG',
            department: 'ERDS',
            shortUrl: 'SEG-00001542',
            longUrl: 'MS-MS-sms-inhouse-tiny-bsp-ec',
            expiryDate: '2025-10-18',
            status: 'Active', // Expired / Active
            createdBy: 'Esha Choudhary - echoudary',
            createdDate: '14 Oct 2025 18:07',
            statusColClasses: ' executed-status ',
            isCopyView: true,
            isRedirectView: true,
          },
        ],
        error: [],
      },
    };

    if (!mockResponse.data.error || mockResponse.data.error.length === 0) {
      const data = mockResponse.data.filteredResponse;
      if (data?.length) {
        this.data = this.preProcessGridData(data);
      }
      this.dataCopy = [...this.data];
      this.totalRecords = mockResponse.data?.page?.totalRecords ?? 0;
      this.totalRecordsInDb = this.totalRecords;
      // this.commonTable.scrollTo({
      //   top: 0,
      //   left: 0,
      //   behavior: 'smooth',
      // });
    } else {
      this.data = [];
      this.dataCopy = [...this.data];
      this.totalRecords = 0;
      this.totalRecordsInDb = this.totalRecords;
      // this.commonService.showErrorMessage(res.data.error[0].errorMessage);
    }
    // },
    // error: (err: HttpErrorResponse) => {
    //   this.totalRecords = 0;
    //   this.totalRecordsInDb = 0;
    //   this.commonService.showError(err);
    // },
    // });
  }

  preProcessGridData(data: any[], isOrchestrationSubTab = false) {
    switch (this.gridName) {
      case GRID_NAME.WABA_LISTING:
        return this.gridService.processWabaListing(data);
      case GRID_NAME.FORM_LISTING:
        return this.gridService.processFormListing(data);
      case GRID_NAME.SEGMENT_LISTING:
        return this.gridService.processSegmentListing(data);
      case GRID_NAME.SCHEDULER_LISTING:
        return this.gridService.processSchedulerListing(data);
      case GRID_NAME.CAMPAIGN_LISTING:
        return this.gridService.processCampaignListing(data);
      case GRID_NAME.FILE_UPLOAD_LISTING:
        return this.gridService.processFileUploadListing(data);
      case GRID_NAME.TEMPLATE_LISTING:
        return this.gridService.processTemplateListing(data);
      case GRID_NAME.SENDER_EMAIL_LISTING:
        return this.gridService.processSenderEmailListing(data);
      case GRID_NAME.SHORTEN_URL_LISTING:
        return this.gridService.processShortenUrlListing(data);
      case GRID_NAME.API_SCHEDULER_LISTING:
        return this.gridService.processApiSchedulerListing(
          data,
          isOrchestrationSubTab,
        );
      default:
        return data;
    }
  }

  onButtonClick(rowData: any, eventName: string) {
    this.onButtonClickEvent.emit({ rowData, eventName });
  }

  onCellClick(rowData: any, header: GridHeader) {
    this.onCellClickEvent.emit({ rowData, header });
  }

  onPagination(event: LazyLoadEvent): void {
    this.pageSize = event.rows!;
    const pageNumber = Math.floor(event.first! / event.rows!);

    if (this.apiConfig.methodType === METHOD_TYPE.POST) {
      this.apiConfig.requestBody = {
        ...this.apiConfig.requestBody,
        pageNumber,
        pageSize: event.rows!,
      };
    } else if (this.apiConfig.methodType === METHOD_TYPE.POST1) {
      this.apiConfig.requestBody = {
        ...this.apiConfig.requestBody,
        page: pageNumber,
        size: event.rows!,
      };
    }

    // Save pagination state if enabled (convert 0-based to 1-based)
    if (this.enableStateStorage) {
      this.savePaginationState(pageNumber + 1, event.rows!);
    }

    this.getGridData();
  }

  onSort(event: SortEvent): void {
    this.sortingField = event.field;
    if (event.order === 1) {
      this.sortingDirection = 1;
    } else {
      this.sortingDirection = -1;
    }
    this.sortData();

    // Save sort state if state storage is enabled
    if (this.enableStateStorage) {
      this.saveCurrentState();
    }
  }

  sortData() {
    if (this.sortingField) {
      this.data.sort((a, b) => {
        const value1 = a[this.sortingField as keyof typeof a];
        const value2 = b[this.sortingField as keyof typeof b];
        let result = null;
        if (!value1 || !value2) result = -1;
        else if (value1 != null && value2 == null) result = 1;
        else if (value1 == null && value2 == null) result = 0;
        else if (this.isDate(value1) && this.isDate(value2)) {
          const date1 = new Date(value1);
          const date2 = new Date(value2);
          return this.sortingDirection * (date1.getTime() - date2.getTime());
        } else if (typeof value1 === 'string' && typeof value2 === 'string')
          result = value1.localeCompare(value2);
        else result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;
        return this.sortingDirection * result;
      });
    }
  }

  isDate(value: any): boolean {
    return value instanceof Date || !isNaN(Date.parse(value));
  }

  applyFilter(event: any): void {
    // Store current filters for state management
    this.currentFilters = event.filters;

    if (this.hasFilter(event.filters)) {
      if (this.data.length === this.dataCopy.length) {
        this.dataCopy = JSON.parse(JSON.stringify(this.data));
      }
      this.data = this.filterPipe.transform(this.dataCopy, event.filters);
      this.totalRecords = this.data.length;
    } else {
      this.data = this.dataCopy;
      this.totalRecords = this.totalRecordsInDb;
    }

    // Save filter state if state storage is enabled
    if (this.enableStateStorage) {
      this.saveCurrentState();
    }
  }

  hasFilter(filters: any) {
    let empty = true;
    if (Object.keys(filters).length === 0) {
      return false;
    } else {
      for (const prop in filters) {
        if (filters[prop]) {
          filters[prop].forEach((filterObj: any) => {
            if (filterObj?.value?.length > 0) {
              empty = false;
            }
          });
        }
      }
      return !empty;
    }
  }

  resetFilter(): void {
    if (this.commonTable) {
      this.commonTable.clear();
      // Clear current filter state
      this.currentFilters = undefined;

      // Save the cleared filter state if state storage is enabled
      if (this.enableStateStorage) {
        this.saveCurrentState();
      }
    }
  }

  getCurrentTableData() {
    return JSON.parse(JSON.stringify(this.data));
  }

  updateData(data: any[], keyName: string) {
    const dataMap: any = {};
    data.forEach((obj: any) => {
      dataMap[obj[keyName]] = obj;
    });
    const arr = [];
    for (const obj of this.data) {
      if (dataMap[obj[keyName]]) {
        arr.push(dataMap[obj[keyName]]);
      } else {
        arr.push(obj);
      }
    }
    this.data = arr;
  }

  /**
   * Initialize all state (pagination, filters, sorting) from stored state
   */
  private initializeStateFromStorage(): void {
    const currentRoute = this.getCurrentRoute();
    if (!currentRoute || !this.gridName) {
      return;
    }

    const stateKey = `${currentRoute}_${this.gridName}`;
    const savedState = this.paginationStateService.getExtendedState(stateKey);

    // Restore pagination state
    if (savedState.pageNumber !== 1 || savedState.pageSize !== 10) {
      // Convert 1-based page number to 0-based for PrimeNG/API
      const zeroBasedPageNumber = savedState.pageNumber - 1;

      // Apply saved state to API config
      if (this.apiConfig.methodType === METHOD_TYPE.POST) {
        this.apiConfig.requestBody = {
          ...this.apiConfig.requestBody,
          pageNumber: zeroBasedPageNumber,
          pageSize: savedState.pageSize,
        };
      } else if (this.apiConfig.methodType === METHOD_TYPE.POST1) {
        this.apiConfig.requestBody = {
          ...this.apiConfig.requestBody,
          page: zeroBasedPageNumber,
          size: savedState.pageSize,
        };
      }

      this.pageSize = savedState.pageSize;
    }

    // Restore filter state
    if (savedState.filters) {
      this.currentFilters = savedState.filters;
    }

    // Restore sorting state
    if (savedState.sortField && savedState.sortDirection !== undefined) {
      this.sortingField = savedState.sortField;
      this.sortingDirection = savedState.sortDirection;
    }

    // Emit channel and tab change events if saved state exists
    if (
      savedState.selectedChannel &&
      savedState.selectedChannel !== this.selectedChannel
    ) {
      this.onChannelChangeEvent.emit(savedState.selectedChannel);
    }

    if (savedState.tabType && savedState.tabType !== this.tabType) {
      this.onTabChangeEvent.emit(savedState.tabType);
    }
  }

  /**
   * Save current pagination state
   */
  private savePaginationState(pageNumber: number, pageSize: number): void {
    this.saveCurrentState(pageNumber, pageSize);
  }

  /**
   * Save complete current state including pagination, filters, and sorting
   */
  private saveCurrentState(pageNumber?: number, pageSize?: number): void {
    const currentRoute = this.getCurrentRoute();
    if (!currentRoute || !this.gridName) {
      return;
    }

    // Calculate current page info if not provided
    const currentPageNumber =
      pageNumber !== undefined
        ? pageNumber
        : Math.floor(this.apiConfig?.requestBody?.pageNumber || 0) + 1;
    const currentPageSize =
      pageSize !== undefined
        ? pageSize
        : this.apiConfig?.requestBody?.pageSize || this.pageSize || 10;

    const stateKey = `${currentRoute}_${this.gridName}`;
    this.paginationStateService.saveExtendedState(
      stateKey,
      currentPageNumber,
      currentPageSize,
      this.selectedChannel,
      this.tabType,
      {
        filters: this.currentFilters,
        sortField: this.sortingField,
        sortDirection: this.sortingDirection,
      },
    );
  }

  /**
   * Restore table pagination state after data loading
   */
  private restoreTableState(): void {
    const currentRoute = this.getCurrentRoute();
    if (!currentRoute || !this.gridName) {
      return;
    }

    const stateKey = `${currentRoute}_${this.gridName}`;
    const savedState = this.paginationStateService.getExtendedState(stateKey);

    // Use setTimeout to ensure table is fully rendered
    setTimeout(() => {
      if (this.commonTable) {
        // Restore pagination state
        if (savedState.pageNumber !== 1 || savedState.pageSize !== 10) {
          const zeroBasedPageNumber = savedState.pageNumber - 1;
          // Calculate the first record index for PrimeNG
          const first = zeroBasedPageNumber * savedState.pageSize;
          this.commonTable.first = first;
          this.commonTable.rows = savedState.pageSize;

          // Force table to update its pagination display
          this.commonTable.totalRecords = this.totalRecords;
        }

        // Restore filter state
        if (savedState.filters) {
          // Apply the saved filters to the table
          this.commonTable.filters = savedState.filters;
          this.currentFilters = savedState.filters;

          // Apply the filter logic to the data
          if (this.hasFilter(savedState.filters)) {
            if (this.data.length === this.dataCopy.length) {
              this.dataCopy = [...this.data];
            }
            this.data = this.filterPipe.transform(
              this.dataCopy,
              savedState.filters,
            );
            this.totalRecords = this.data.length;
          }
        }

        // Restore sorting state
        if (savedState.sortField && savedState.sortDirection !== undefined) {
          this.commonTable.sortField = savedState.sortField;
          this.commonTable.sortOrder = savedState.sortDirection;
          this.sortingField = savedState.sortField;
          this.sortingDirection = savedState.sortDirection;

          // Apply the sorting to the current data
          this.sortData();
        }
      }
    }, 200);
  }

  /**
   * Clear stored pagination state for this grid
   */
  public clearPaginationState(): void {
    const currentRoute = this.getCurrentRoute();
    if (!currentRoute || !this.gridName) {
      return;
    }

    const stateKey = `${currentRoute}_${this.gridName}`;
    this.paginationStateService.clearState(stateKey);
  }

  /**
   * Get current route path, handling both base routes and parameterized routes
   */
  private getCurrentRoute(): string {
    const url = this.router.url;
    // Remove query parameters and get base path
    const basePath = url.split('?')[0];
    // For parameterized routes like /templates/123, get the base route /templates
    const pathSegments = basePath.split('/');

    // Handle common routing patterns
    if (pathSegments.length >= 2) {
      // For routes like /templates/123 or /campaigns/456, return /templates or /campaigns
      return `/${pathSegments[1]}`;
    }

    return basePath;
  }
}
